import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { GlobalBetSlip } from "@/components/GlobalBetSlip";
import { Icon } from "@/components/Icon";
import { openCookiePreferences } from "@/lib/cookieConsent";
import wordmark from "../../assets/pnrbet-wordmark.png";
import iconMark from "../../assets/pnrbet-icon.png";

/* PRYMEBET redesign shell — replaces the 128px Navbar with a desktop sidebar +
   slim top bar and a mobile top bar + bottom tab bar. Faithful to the Claude
   Design shell components (PNRSidebar/PNRTopbar/PNRTabBar/PNRMobileTop).
   Presentation only — data/logic come from AuthContext + react-router. */

const NAV_GROUPS = [
  {
    title: "Play",
    items: [
      { key: "home", label: "Home", icon: "home", to: "/" },
      { key: "sportsbook", label: "Sportsbook", icon: "sportsbook", to: "/sportsbook" },
      { key: "live", label: "Live In-Play", icon: "live", to: "/sportsbook?view=live" },
      { key: "casino", label: "Casino", icon: "casino", to: "/casino" },
      { key: "livedealer", label: "Live Dealer", icon: "livedealer", to: "/casino?tab=live" },
    ],
  },
  {
    title: "Rewards",
    items: [
      // One "Rewards" entry — Daily Reward / VIP Club / Refer & Earn were separate links that all
      // opened the same /rewards screen, so they're consolidated here (the page still has those sections).
      // Labeled "Rewards" (not "Promotions") with the rewards icon so the sidebar matches the bottom TabBar.
      { key: "promos", label: "Rewards", icon: "rewards", to: "/rewards" },
    ],
  },
  {
    title: "You",
    items: [
      { key: "mybets", label: "My Bets", icon: "mybets", to: "/my-bets" },
      { key: "wallet", label: "Wallet", icon: "wallet", to: "/wallet" },
      { key: "support", label: "Support", icon: "support", to: "/support" },
    ],
  },
];

const TABS = [
  { key: "home", label: "Home", icon: "home", to: "/" },
  { key: "sportsbook", label: "Sports", icon: "sportsbook", to: "/sportsbook" },
  { key: "casino", label: "Casino", icon: "casino", to: "/casino" },
  { key: "mybets", label: "My Bets", icon: "mybets", to: "/my-bets" },
  { key: "rewards", label: "Rewards", icon: "rewards", to: "/rewards" },
];

function activeKey(pathname, search, hash) {
  if (pathname.startsWith("/sportsbook") || pathname.startsWith("/event")) {
    return /view=live/.test(search) ? "live" : "sportsbook";
  }
  if (pathname.startsWith("/casino")) {
    if (/tab=originals/.test(search)) return "originals";
    if (/tab=live/.test(search)) return "livedealer";
    return "casino";
  }
  if (pathname.startsWith("/my-bets")) return "mybets";
  if (pathname.startsWith("/support")) return "support";
  if (pathname.startsWith("/rewards")) return "promos";
  if (pathname.startsWith("/account")) return "account";
  if (pathname.startsWith("/store") || pathname.startsWith("/wallet")) return "wallet";
  if (pathname === "/") return "home";
  return "";
}

const Avatar = ({ user, size = 40 }) => {
  const initials = (user?.username || user?.email || "PN").slice(0, 2).toUpperCase();
  // Honest VIP progress: real value or 0 (neutral fallback) — never a fabricated 62%.
  const pct = Math.max(0, Math.min(100, Number(user?.vip?.progress) || 0));
  return (
    <div
      className="cursor-pointer transition duration-150 hover:scale-[1.04] hover:drop-shadow-[0_0_5px_rgba(255,255,255,0.55)]"
      style={{
        width: size, height: size, borderRadius: "50%", padding: 2, boxSizing: "border-box",
        background: `conic-gradient(#E4E4E7 0 ${pct}%, #27272A ${pct}% 100%)`,
      }}
    >
      <div className="flex h-full w-full items-center justify-center rounded-full border-2 border-surface-1 bg-surface-5 font-display text-[13px] font-extrabold text-[#E4E4E7]">
        {initials}
      </div>
    </div>
  );
};

/* AccountMenu — the avatar dropdown. Keeps the 5-tab bottom nav uncrowded (§4.4) by making the
   secondary destinations (Wallet, Account, Support, Legal, Responsible Play, Cookie Preferences,
   and admin) reachable without adding a 6th tab. Closes on outside-click, Escape, and route
   change; fully keyboard-focusable. */
function AccountMenu({ user, size = 44 }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  const location = useLocation();
  const isAdmin = user?.role === "admin" || user?.role === "owner" || user?.role === "super_admin";

  React.useEffect(() => { setOpen(false); }, [location.pathname, location.search, location.hash]);
  React.useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const items = [
    { label: "My account", icon: "vip", to: "/account" },
    { label: "Wallet", icon: "wallet", to: "/wallet" },
    { label: "My Bets", icon: "mybets", to: "/my-bets" },
    { label: "Support", icon: "support", to: "/support" },
    ...(isAdmin ? [{ label: "Admin", icon: "shield-check", to: "/admin" }] : []),
  ];

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        type="button"
        aria-label="Account menu"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="rounded-full focus-ring"
        data-testid="shell-account-menu-trigger"
      >
        <Avatar user={user} size={size} />
      </button>
      {open && (
        <div
          role="menu"
          data-testid="shell-account-menu"
          className="absolute right-0 top-[calc(100%+8px)] z-50 w-56 overflow-hidden rounded-2xl border border-surface-border bg-surface-2 p-1.5 shadow-[var(--elev-4)]"
        >
          <div className="px-2.5 py-2">
            <p className="truncate font-display text-sm font-extrabold text-white">{user?.username || "Player"}</p>
            {user?.email && <p className="truncate text-[11px] text-meta">{user.email}</p>}
          </div>
          <div className="my-1 h-px bg-surface-border" />
          {items.map((it) => (
            <Link
              key={it.to}
              to={it.to}
              role="menuitem"
              className="flex items-center gap-2.5 rounded-[10px] px-2.5 py-2 text-sm font-medium text-muted-foreground hover:bg-white/[0.04] hover:text-white focus-ring"
            >
              <Icon name={it.icon} size={16} /> <span className="flex-1">{it.label}</span>
            </Link>
          ))}
          <div className="my-1 h-px bg-surface-border" />
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-2.5 py-2 text-[11px] font-medium text-[#71717a]">
            <Link to="/legal" className="hover:text-white">Legal</Link>
            <Link to="/responsible-play" className="hover:text-white">Responsible Play</Link>
            <button type="button" onClick={openCookiePreferences} data-testid="shell-cookie-prefs-menu" className="hover:text-white">Cookies</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Sidebar({ user, active }) {
  const isCasino = ["casino", "originals", "livedealer"].includes(active);
  const groups = React.useMemo(() => {
    const g = NAV_GROUPS.map((grp) => ({ ...grp }));
    if (user?.role === "admin" || user?.role === "owner" || user?.role === "super_admin") {
      g[2] = { ...g[2], items: [...g[2].items, { key: "admin", label: "Admin", icon: "shield-check", to: "/admin" }] };
    }
    return g;
  }, [user?.role]);

  return (
    <aside className="surface-premium-rail hidden h-screen w-[248px] shrink-0 flex-col gap-3 overflow-y-auto border-r border-surface-border px-3.5 pb-3.5 pt-4 text-white lg:flex">
      <div className="flex items-center justify-between px-1.5 pt-1">
        <Link to="/"><img src={wordmark} alt="PRYMEBET" className="h-12 w-auto" /></Link>
      </div>

      <div className="grid grid-cols-2 gap-1.5 rounded-2xl border border-surface-border bg-surface-4 p-1">
        <Link to="/sportsbook" className={`flex h-9 items-center justify-center rounded-[10px] text-[13px] ${!isCasino ? "bg-pnr-green font-bold text-surface-1" : "font-semibold text-muted-foreground"}`}>Sports</Link>
        <Link to="/casino" className={`flex h-9 items-center justify-center rounded-[10px] text-[13px] ${isCasino ? "bg-pnr-green font-bold text-surface-1" : "font-semibold text-muted-foreground"}`}>Casino</Link>
      </div>

      {groups.map((grp) => (
        <div key={grp.title} className="flex flex-col gap-0.5">
          <div className="px-2.5 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#52525b]">{grp.title}</div>
          {grp.items.map((n) => {
            const on = active === n.key;
            return (
              <Link
                key={n.key}
                to={n.to}
                className={`flex h-[34px] items-center gap-3 rounded-[10px] px-2.5 text-sm transition-colors ${
                  on
                    ? "bg-pnr-green/[0.07] font-semibold text-white shadow-[var(--glow-green-edge),0_4px_14px_-8px_rgba(0,214,50,0.45)]"
                    : "font-medium text-muted-foreground hover:bg-[#00B829]/5 hover:text-white"
                }`}
              >
                <Icon name={on ? `${n.icon}-fill` : n.icon} size={18} className={on ? "text-pnr-green" : ""} />
                <span className="flex-1">{n.label}</span>
              </Link>
            );
          })}
        </div>
      ))}

      <div className="surface-premium mt-auto flex flex-col gap-2.5 rounded-2xl p-3.5">
        <div className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-[10px] bg-[#E4E4E7]">
            <Icon name="vip-fill" size={18} className="text-surface-1" />
          </span>
          <div className="flex-1">
            <div className="font-display text-sm font-extrabold leading-tight text-[#E4E4E7]">{user?.vip?.tier || "Bronze"} · Lvl {user?.vip?.level ?? 1}</div>
            <div className="text-[11px] font-medium leading-snug text-muted-foreground">{user?.vip?.progressLabel || "Level up to earn more"}</div>
          </div>
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-surface-border">
          <div className="h-full rounded-full bg-[#E4E4E7]" style={{ width: `${Math.max(0, Math.min(100, Number(user?.vip?.progress) || 0))}%` }} />
        </div>
      </div>

      <div className="flex flex-col gap-1 px-1.5 text-[11px] font-medium text-[#52525b]">
        <div className="flex items-center justify-between">
          <span>18+ · Play responsibly</span>
          <span>No purchase necessary</span>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
          <Link to="/legal" className="hover:text-white">Legal</Link>
          <button type="button" onClick={openCookiePreferences} data-testid="shell-cookie-prefs" className="hover:text-white">Cookie Preferences</button>
          <Link to="/responsible-play" className="hover:text-white">Responsible Play</Link>
        </div>
      </div>
    </aside>
  );
}

function TopBar({ user, mask }) {
  // Context-aware search: on the casino, open casino game/provider search; elsewhere, sports/market
  // search. Keyboard hint matches the behavior that actually fires for the current surface.
  const { pathname } = useLocation();
  const isCasino = pathname.startsWith("/casino");
  const openSearch = () =>
    window.dispatchEvent(new CustomEvent(isCasino ? "pnr-open-casino-search" : "pnr-open-sb-search"));
  return (
    <header className="sticky top-0 z-30 hidden h-[72px] items-center gap-4 border-b border-surface-border bg-surface-1/80 px-7 text-white backdrop-blur-xl lg:flex">
      <button type="button" data-testid="topbar-search" onClick={openSearch}
        className="flex h-[42px] min-w-0 flex-[0_1_380px] items-center gap-2.5 rounded-xl border border-surface-border bg-surface-4 px-3.5 text-left text-[#71717a] hover:text-[#a1a1aa] focus-ring">
        <Icon name="search" size={16} />
        <span className="flex-1 truncate text-sm font-medium">{isCasino ? "Search games or providers…" : "Search teams, leagues, games…"}</span>
        <span className="rounded-md border border-surface-border px-1.5 text-[11px] font-semibold">⌘K</span>
      </button>
      <div className="flex-1" />
      <div className="flex h-11 items-center gap-1 rounded-2xl border border-surface-border bg-surface-4 p-1">
        <Link to="/wallet" aria-label="View coins balance" className="flex h-9 items-center gap-2 rounded-[10px] bg-pnr-green/10 px-3 shadow-[inset_0_0_0_1px_rgba(0,214,50,0.35)] focus-ring">
          <Icon name="pnrcoin" size={22} className="text-pnr-coin" />
          <span className="font-display text-[15px] font-extrabold tabular-nums text-white">{mask((user?.coins || 0).toFixed(2))}</span>
          <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-pnr-green">Coins</span>
        </Link>
        <Link to="/wallet" aria-label="View cash balance" className="flex h-9 items-center gap-2 rounded-[10px] px-3 focus-ring">
          <Icon name="pnrcash" size={22} className="text-pnr-cash" />
          <span className="font-display text-[15px] font-extrabold tabular-nums text-[#e4e4e7]">{mask((user?.cash || 0).toFixed(2))}</span>
          <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground">Cash</span>
        </Link>
      </div>
      <Link to="/store" className="flex h-11 items-center gap-2 rounded-xl bg-pnr-green px-[18px] font-display text-sm font-extrabold text-surface-1 focus-ring">
        <Icon name="plus" size={16} /> Get Coins
      </Link>
      <button className="relative flex h-11 w-11 items-center justify-center rounded-xl border border-surface-border bg-surface-4 text-muted-foreground focus-ring" aria-label="Notifications">
        <Icon name="bell" size={18} />
      </button>
      <AccountMenu user={user} size={40} />
    </header>
  );
}

function MobileTop({ user, mask }) {
  // ~56px bar with safe-area top padding (§4.2). The balance is a neutral VIEWING pill
  // (surface-4, no green fill — it must not look like the play-wallet selector); it shows the
  // primary (coins) balance and routes to /wallet. The green "+" is the separate get-coins CTA.
  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-surface-border bg-surface-1/90 px-3 pt-[env(safe-area-inset-top)] backdrop-blur-xl lg:hidden">
      <Link to="/" aria-label="Home" className="shrink-0 rounded-md focus-ring"><img src={iconMark} alt="PRYMEBET" className="h-9 w-9" /></Link>
      <div className="flex-1" />
      {/* Balance read-out PILL — neutral (viewing, not selecting), taps to Wallet. */}
      <Link
        to="/wallet"
        aria-label="View wallet"
        data-testid="mobile-balance-pill"
        className="flex h-10 items-center gap-1.5 rounded-xl border border-surface-border bg-surface-4 px-2.5 focus-ring"
      >
        <Icon name="pnrcoin" size={18} className="text-pnr-coin" />
        <span className="font-display text-sm font-extrabold tabular-nums text-white">{mask((user?.coins || 0).toFixed(2))}</span>
      </Link>
      {/* Get-coins CTA — the one green affordance here. */}
      <Link to="/store" aria-label="Get coins" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-pnr-green focus-ring">
        <Icon name="plus" size={16} className="text-surface-1" />
      </Link>
      <AccountMenu user={user} size={40} />
    </header>
  );
}

function TabBar({ active }) {
  // 5 tabs, ≥44px targets, active = green -fill icon in a bg-pnr-green/14 pill + green label.
  // Safe-area bottom padding so the bar clears the home indicator on notched phones (§4.4).
  return (
    <nav className="surface-premium-rail sticky bottom-0 z-30 grid grid-cols-5 border-t border-surface-border px-2 pb-[env(safe-area-inset-bottom)] pt-1.5 backdrop-blur-xl lg:hidden">
      {TABS.map((t) => {
        const on = active === t.key || (t.key === "rewards" && active === "promos");
        return (
          <Link
            key={t.key}
            to={t.to}
            aria-current={on ? "page" : undefined}
            className={`flex min-h-[56px] flex-col items-center justify-center gap-1 rounded-xl text-[10px] focus-ring ${on ? "font-bold text-pnr-green" : "font-semibold text-[#71717a]"}`}
          >
            <span className={`flex h-7 w-11 items-center justify-center rounded-full transition-shadow ${on ? "bg-pnr-green/[0.14] shadow-[var(--glow-green-edge)]" : ""}`}>
              <Icon name={on ? `${t.icon}-fill` : t.icon} size={20} className={on ? "text-pnr-green" : ""} />
            </span>
            {t.label}
          </Link>
        );
      })}
    </nav>
  );
}

export function Shell({ children }) {
  const { user, streamerMode } = useAuth();
  const location = useLocation();
  const mask = (s) => (streamerMode ? "•••" : s);
  const active = activeKey(location.pathname, location.search, location.hash);

  return (
    <div className="flex min-h-screen bg-surface-1 text-white">
      <Sidebar user={user} active={active} />
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar user={user} mask={mask} />
        <MobileTop user={user} mask={mask} />
        <main className="relative z-10 flex-1 pb-[calc(68px+env(safe-area-inset-bottom))] lg:pb-0">{children}</main>
        <TabBar active={active} />
      </div>
      <GlobalBetSlip />
    </div>
  );
}

export default Shell;
